import { UserProfile } from 'fitbeat.gym-core';
import { BaseUserProfile } from 'fitbeat.models';
import React, { Component } from 'react';
import { translate } from 'react-i18next';
import {
  AppState,
  AppStateStatus,
  Platform, StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';
import { connect } from 'react-redux';
import { TranslateProp } from '../../common-types';
import ConnectedWorkoutApp, { AuthorizationStatus } from '../../ConnectedApps/connected-workout-app';
import theme from '../../theme';
import { UserDataConsumer } from '../../utils';
import { defaultScale } from '../../utils/scaling';
import WorkoutActionsTest from './WorkoutActionsTest';
import { showMessage } from '../../ui/components/Toast';
import { getConnectedAppIcon } from '../../ConnectedApps/ui';
import { garminIsEnabledForUserSelector } from '../../auth/auth-selectors';
import { LoadingModal } from '../../ui';
import { connectToApp } from '../../auth/app-onboarding/connected-apps/utils';

interface IProps {
  t: TranslateProp;
  user: any /* UserDataConsumer */;
  connectedAppsList: ConnectedWorkoutApp[];
  appDetails: ConnectedWorkoutApp;
  isLastItem?: boolean;
  appCallback: () => void;
  garminAuthorizationRequest: () => void;
  garminIsEnabledForUser?: boolean;
}

interface IState {
  appIsEnabled: boolean;
  appState: AppStateStatus;
  appIsLoading: boolean;
}

class ConnectedAppTile extends Component<IProps, IState> {
  constructor(props) {
    super(props);
    this.state = {
      appIsEnabled: false,
      appState: AppState.currentState,
      appIsLoading: false,
    };
  }

  public async componentDidMount() {
    await this.updateAuthState();
    AppState.addEventListener('change', this.updateAuthState);
  }

  public componentWillUnmount() {
    AppState.removeEventListener('change', this.updateAuthState);
  }

  public componentDidUpdate(): void {
    const garminAuthCancelled = this.props.appDetails.name === 'Garmin Connect'
      && this.state.appIsLoading
      && this.props.garminIsEnabledForUser === false;
    if (garminAuthCancelled) {
      this.setState({
        appIsLoading: false,
      });
    }
  }

  public updateAuthState = async () => {
    if (this.props.appDetails.name === 'Apple Health') {
      this.props.appCallback();
    }
    const checkIfAppIsEnabled = () => {
      if (this.props.appDetails.name !== 'Garmin Connect') {
        return healthKitStatus === AuthorizationStatus.authorized;
      }
      return UserProfile.hasValidWorkoutImportConfig(this.props.user.userProfile, 'garmin-connect');
    };
    const healthKitStatus = await this.props.appDetails.authorizationStatus();
    this.setState({
      appIsEnabled: checkIfAppIsEnabled(),
    });
    if (this.state.appIsLoading && this.state.appIsEnabled) {
      this.setState({
        appIsLoading: false,
      });
    }
    return healthKitStatus;
  };

  public requestAuth = async () => {
    const result = await connectToApp(this.props.appDetails);

    if (result === 'success') {
      await this.updateAuthState();
      if (this.state.appIsLoading && this.state.appIsEnabled) {
        this.setState({ appIsLoading: false });
      }
    } else if (result === 'loading') {
      this.setState({ appIsLoading: true });
    } else if (result === 'error') {
      // error is handled as a toast message using errorDisplayMessage inside connectToApp
    } else {
      showMessage(result);
    }
  };

  public showToast = () => {
    const { t } = this.props;
    showMessage(t('updateExternalSettings', { appName: this.props.appDetails.name }));
  };

  public renderConnectedAppConnectionType(appDetails: ConnectedWorkoutApp, t: TranslateProp) {
    const { garminIsEnabledForUser } = this.props;
    const { appIsEnabled, appIsLoading } = this.state;
    const garminIsEnabled = garminIsEnabledForUser && appDetails.name === 'Garmin Connect';
    if (appIsEnabled || garminIsEnabled) {
      return (
        <TouchableOpacity onPress={this.showToast}>
          <Text style={styles.connectedButtonContainer}>
            {t('manage')}
          </Text>
        </TouchableOpacity>
      );
    }
    if (appIsLoading) {
      return (
        <View>
          <LoadingModal isLoading />
        </View>
      );
    }
    return (
      <TouchableOpacity onPress={this.requestAuth}>
        <Text style={[styles.saveButtonContainer, { color: theme.PRIMARY }]}>
          {t('connect')}
        </Text>
      </TouchableOpacity>
    );
  }

  public render() {
    const {
      t,
      appDetails,
      user: { userProfile },
      isLastItem = false,
    } = this.props;

    return (
      <View>
        <View style={[styles.itemContainer, isLastItem && { borderBottomWidth: 0 }]}>
          <View style={styles.iconLabelContainer}>
            {getConnectedAppIcon(appDetails.name, defaultScale(28), styles.connectedAppIcon)}
            <Text
              style={[
                theme.textStyle.bodyCopyReg,
                styles.connectedAppItemHeading,
              ]}
            >
              {appDetails.name}
            </Text>
          </View>
          {this.renderConnectedAppConnectionType(appDetails, t)}
        </View>
        {BaseUserProfile.isFeatureEnabledForUser('alpha', userProfile) && (
          <WorkoutActionsTest connectedApp={appDetails} />
        )}
      </View>
    );
  }
}

const styles = StyleSheet.create({
  itemContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: defaultScale(20),
    borderBottomWidth: 1,
    borderColor: theme.GREY_LIGHT,
  },
  saveButtonContainer: {
    ...theme.textStyle.bodyCopyBold,
    textAlign: 'center',
  },
  connectedButtonContainer: {
    ...theme.textStyle.bodyCopyBold,
    textAlign: 'center',
    color: theme.GREEN,
  },
  iconLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  connectedAppItemHeading: {
    paddingHorizontal: defaultScale(10),
  },
  noBottomBorder: {
    borderColor: 'red',
  },
  connectedAppIcon: {
    shadowOffset: { width: 0, height: 1 },
    shadowColor: theme.BLACK,
    shadowRadius: 2,
    shadowOpacity: 0.1,
  },
});

const mapStateToProps = (state) => ({
  garminIsEnabledForUser: garminIsEnabledForUserSelector(state),
});

export default UserDataConsumer(
  connect(mapStateToProps)(translate(['settings'], { wait: true })(ConnectedAppTile)),
);
